import { selectIsReadOnlyAccess, useCampaignCapabilitiesStore } from '@discovr/features';
import { ActionConfirmDialog, CONFIRM_WORD } from '../../../components/common/ActionConfirmDialog';
import { X } from '../../../icons';
import { rowDisplayName } from '../../utils/rowDisplayName';
import { BulkCreatorLine } from './BulkCreatorLine';
import type { CreatorsRow } from './types';

export interface BulkDeleteDialogProps {
  rows: CreatorsRow[];
  platform: string;
  busy: boolean;
  onClose: () => void;
  /** Drops one creator from the selection; removing the last one closes the dialog. */
  onDeselect: (rowId: string) => void;
  onConfirm: (confirm: string) => Promise<void>;
}

const DELETE_DESCRIPTION =
  'This erases these creators from this campaign along with everything attached to them. They will NOT appear on the Rejected tab and cannot be sent back to Pending — re-import them to start over.';

function deleteTitle(count: number): string {
  if (count === 1) return 'Delete 1 creator permanently?';
  return `Delete ${count} creators permanently?`;
}

function deleteConfirmLabel(count: number): string {
  if (count === 1) return 'Delete 1 creator';
  return `Delete ${count} creators`;
}

/** Admin bulk delete — the single-row delete's dialog (fixed-word gate, no reason) for a selection. */
export function BulkDeleteDialog({ rows, platform, busy, onClose, onDeselect, onConfirm }: BulkDeleteDialogProps) {
  const isReadOnly = useCampaignCapabilitiesStore(selectIsReadOnlyAccess);
  // ActionConfirmDialog only arms confirm once the fixed word matches, so the
  // typed value is always present here; the BE re-checks it either way.
  const handleConfirm = async (_reason?: string, typedConfirm?: { confirm: string }) => {
    await onConfirm(typedConfirm?.confirm ?? '');
  };

  const extraContent = (
    <ul className="space-y-2" aria-label="Creators to delete">
      {rows.map((row) => (
        <li key={String(row.id)} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
          <BulkCreatorLine row={row} platform={platform} />
          <button
            type="button"
            onClick={() => onDeselect(String(row.id))}
            disabled={busy}
            aria-label={`Remove ${rowDisplayName(row)} from selection`}
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
          >
            <X size={14} weight="bold" />
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <ActionConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      variant="delete"
      title={deleteTitle(rows.length)}
      description={DELETE_DESCRIPTION}
      confirmLabel={deleteConfirmLabel(rows.length)}
      extraContent={extraContent}
      canConfirm={!isReadOnly && rows.length > 0}
      confirmText={CONFIRM_WORD}
      busy={busy}
      onConfirm={handleConfirm}
    />
  );
}
